import { BaseResponseType } from '../cognito/authResponse.types';
import { KitchenHubProviders } from '../kitchenHub/kitchenHub.types';
import { RestaurantResponseType } from '../restaurant/restaurant.types';
import { TipConfigurationPaymentType } from '../tip-manager/tip-manager.types';

export interface IKdsPrintConfiguration {
  kdsPrintEnabled: boolean;
  pos: boolean;
  mpos: boolean;
  kiosk: boolean;
  qr: boolean;
  online: boolean;
  tablesideAi: boolean;
  thirdParty: boolean;
}

export interface PrinterReceiptsItemModifier {
  key: string;
  value: boolean;
}
export interface PrinterReceiptsFontSize {
  FOH: FontSizeConfiguration;
  BOH: FontSizeConfiguration;
}

export interface PrinterReceiptsDisplayItemModifier {
  FOH: boolean;
  BOH: boolean;
}

export enum FontSizeConfiguration {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum IncrementIndicator {
  INCREASE = 'Increase',
  DECREASE = 'Decrease',
}

export interface PrinterReceiptSettings {
  printCustomersCopy?: boolean;
  printMerchantsCopy?: boolean;
  showPaymentQR?: boolean;
  showFeedbackQR?: boolean;
  showItemModifiers?: boolean;
  itemModifiers?: PrinterReceiptsItemModifier[];
  showCustomMessage?: boolean;
  customMessage?: string;
  fontSize?: PrinterReceiptsFontSize;
  displayItemModifiers?: PrinterReceiptsDisplayItemModifier;
  isBohKitchenReceiptsEnabled?: boolean;
  displayNotesOnFOHReceipt?: boolean;
  showFooterTextOnCustomerCopy?: boolean;
  customerCopyFooterText?: string;
  showNonPricedModifiersOnFOHReceipts?: boolean;
  customModifierEnabled?: boolean;
}

export interface ThirdPartyPricingSettings {
  provider?: KitchenHubProviders;
  priceIndicator?: IncrementIndicator;
  percentage?: number;
  active?: boolean;
}
export interface ItemModifierOptions {
  showAllModifiers: boolean;
  showModifiersWithExtraCost: boolean;
}

export interface DigitalReceiptSettings {
  showFeedbackSection?: boolean;
  showItemModifiers?: boolean;
  showItemModifierOptions?: ItemModifierOptions;
  enableInstagram?: boolean;
  instagramLink?: string;
  enableFacebook: boolean;
  facebookLink?: string;
  enableTwitter: boolean;
  twitterLink?: string;
  showFooterTextOnCustomerCopy?: boolean;
  customerCopyFooterText?: string;
  showNonPricedModifiersOnFOHReceipts?: boolean;
}

export interface SuggestedTip {
  order: number;
  value: number;
  isDefault?: boolean;
}

export interface TipSettings {
  areTipsEnabled: boolean;
  suggestedTips: SuggestedTip[];
  customTipEnabled?: boolean;
  tipConfigurationPaymentType?: TipConfigurationPaymentType;
}

export interface ServiceChargeSettings {
  stateServiceCharges?: boolean;
  deliveryFee?: boolean;
  largePartyCharge?: boolean;
}
export interface PaymentMethodSettings {
  supportCash: boolean;
  supportCard: boolean;
  dealsDiscount: boolean;
  tender: boolean;
}

export interface salesAndTaxSummaryType {
  totalNetSales: string;
  tax: string;
  creditCardTips: string;
  tenderTips: string;
  giftCardTips: string;
  surcharges: string;
  gratuity: string;
  cashTips: string;
  giftCardsSoldDiscounted: string;
  giftCardsSold: string;
  thirdPartyTips: string;
  total: string;
  grossSales: string;
  discountAmount: string;
}

export interface TipPoolConfigurationType {
  hasActiveTipPool: boolean;
  tipsPaidDaily: boolean;
  tipsPaidThroughPayroll: boolean;
}

interface tipType {
  collectedTips: string;
  paidTips: string;
  pendingTips: string;
}

export interface paymentDetailsType {
  cash?: string | null;
  card?: string | null;
  giftCard?: string | null;
  tender: string | null;
  totalPayments?: string | null;
  processingFees?: string | null;
  doorDash?: string | null;
  grubHub?: string | null;
  uberEats?: string | null;
}

export interface cashDrawerDepositsDataType {
  drawer_id: number;
  drawer_name: string;
  startingValue: number;
  totalCashIn: number;
  totalCashOut: number;
  expectedCash: number;
  actualCash: number;
  cashDrawerOver: number;
}

export interface houseDrawerDepositSummary {
  drawer_id: number | string;
  drawer_name: string;
  startingCash: number;
  drawerReplenishment: number;
  cashInOtherReasons: number;
  bankCashDeposit: number;
  transferToCashDrawers: number;
  tipsOut: number;
  payOut: number;
  cashOut: number;
  expectedCash: number;
  cashDrawers?: {
    drawer_id: number;
    drawer_name: string;
    actualCash: number;
  }[];
}

export interface dailyEmployeeTipsType {
  employeeId: string;
  employeeName: string;
  roleId: number;
  roleName: string;
  tipsBeforePool: number;
  pendingTips: number;
  tipsAfterPool: number;
}

interface serverTipsAndGratuityCollectedType {
  total: string | undefined;
}

interface tipsAndGratuityEarnedByEmployeesType {
  employees: Array<{
    employeeName: string;
    // Case 1: Tip Pool + Closeout
    earned?: string;
    contributed?: string;
    tipOutReceived?: string;
    takeHome?: string;

    // Case 2: Normal tips
    totalTipsAndGratuity?: string;
  }>;
  totals: {
    // Case 1 totals
    totalEarned?: string;
    totalContributed?: string;
    totalTipOutReceived?: string;
    totalTakeHome?: string;

    // Case 2 totals
    totalTipsAndGratuity?: string;
  };
}

export interface employeeCheckoutSummaryType {
  employees: Array<{
    name: string;
    serverOwes: number;
  }>;
  total: number;
}

export interface totalVoidsType {
  voidAmount?: string | null;
  voidOrderCount?: string | null;
  voidItemCount?: string | null;
  voidPercent?: string | null;
}

export interface ZReportFields {
  salesAndTaxSummary: {
    totalNetSales: boolean;
    tax: boolean;
    creditCardTips: boolean;
    surcharges: boolean;
    gratuity: boolean;
  };
  paymentDetailsTaxInclusive: {
    cash: boolean;
    card: boolean;
    tender: boolean;
    giftCard: boolean;
    totalPayments: boolean;
  };
  paymentDetailsTaxExclusive: {
    cash: boolean;
    card: boolean;
    tender: boolean;
    giftCard: boolean;
    totalPayments: boolean;
  };
  serverTipsPaid: { totalCashPayments: boolean };
  tipsHeldForPayroll: { tipsToBePaidOnPayroll: boolean };
  totalVoids: { voidAmount: boolean; voidOrderCount: boolean; voidItemCount: boolean; voidPercent: boolean };
  merchantDeposit: { merchantDeposit: boolean };
  houseDrawerDeposit: { startingValue: boolean; expectedCash: boolean; actualCash: boolean; houseDrawerShort: boolean };
}

export interface IOrderTimerSettings {
  orderDelayedTimerInMinute: number;
  orderDelayedAgainTimerInMinute: number;
  onHoldTimerInMinute: number;
}

export interface generalType {
  [key: string]: string | null | undefined;
}

export interface combinedType
  extends salesAndTaxSummaryType,
    paymentDetailsType,
    serverTipsPaidType,
    tipsHeldForPayrollType,
    totalVoidsType,
    merchantDepositType,
    houseDrawerDepositType,
    generalType {}

export interface totalDiscountsType {
  totalDiscounts: string;
  compDiscounts: string;
  dealDiscounts: string;
  thirdPartyDiscounts: string;
}

export interface ZReportFieldsType {
  salesAndTaxSummary: salesAndTaxSummaryType;
  paymentDetails: paymentDetailsType;
  tip: tipType;
  dailyEmployeeTips: dailyEmployeeTipsType[];
  totalDiscounts: totalDiscountsType;
  serverTipsAndGratuityCollected: serverTipsAndGratuityCollectedType;
  tipsAndGratuityEarnedByEmployee: tipsAndGratuityEarnedByEmployeesType;
  employeeCheckoutSummary: employeeCheckoutSummaryType;
  cashDrawerDepositsData: cashDrawerDepositsDataType[];
  // paymentDetailsTaxExclusive: paymentDetailsType;
  serverTipsAndGratuityPaid: serverTipsPaidType;
  tipsAndGratuityHeldForPayroll: tipsHeldForPayrollType;
  totalVoids: totalVoidsType;
  merchantDeposit: merchantDepositType;
  houseDrawerDeposit: houseDrawerDepositType;
  houseDrawerDepositsData: houseDrawerDepositSummary;
  tipPoolConfiguration: TipPoolConfigurationType;
}

export interface merchantDepositType {
  merchantDeposit: string;
}

export interface serverTipsPaidType {
  totalCashPayments: string;
}

export interface tipsHeldForPayrollType {
  tipsAndGratuityToBePaidOnPayroll: string;
}

export interface houseDrawerDepositType {
  startingValue: string;
  expectedCash: string;
  actualCash: string;
  houseDrawerShort: string;
}

export interface PaymentSettings {
  pos: PaymentMethodSettings;
  mpos: PaymentMethodSettings;
  kiosk: Omit<PaymentMethodSettings, 'tender'>; // Kiosk does not include 'tender'
}
export interface UpdateGeneralSettingsType {
  paymentSettings?: PaymentSettings;
  printerReceiptSettings?: PrinterReceiptSettings;
  serviceChargeSettings?: ServiceChargeSettings;
  tipSettings?: TipSettings;
  showFeedbackScreen?: boolean;
  giftCardsEnabled?: boolean;
  dealsEnabled?: boolean;
}

export interface CreateGeneralSettingsType {
  restaurantId: number;
  locationId: number;
  paymentSettings?: PaymentSettings;
  printerReceiptSettings?: PrinterReceiptSettings;
  digitalReceiptSettings?: DigitalReceiptSettings;
  serviceChargeSettings?: ServiceChargeSettings;
  tipSettings?: TipSettings;
  showFeedbackScreen?: boolean;
  giftCardsEnabled?: boolean;
  dealsEnabled?: boolean;
}

export interface GeneralSettingsType {
  id: number;
  paymentSettings?: PaymentSettings;
  printerReceiptSettings: PrinterReceiptSettings;
  digitalReceiptSettings: DigitalReceiptSettings;
  serviceChargeSettings: ServiceChargeSettings;
  tipSettings: TipSettings;
  createdAt: Date;
  updatedAt: Date;
  restaurant?: RestaurantResponseType;
  thirdPartyPricing?: ThirdPartyPricingSettings;
  kdsPrintConfiguration?: IKdsPrintConfiguration;
}

export interface UpdateGeneralOnlineSettingsType {
  onlinOrderingSettings: {
    enableOnlineOrdering: boolean;
    isDeliveryEnabled: boolean;
    isPickupEnabled: boolean;
    enableScheduledOrders: boolean;
    enablePaylater: boolean;
  };
  businessSetting: {
    businessId: number;
    businessAlias: string;
  };
  restaurantSettings: {
    address: string;
    latitude: number;
    longitude: number;
  };
  onlineItemSettings: {
    itemId: [];
    showOnline: boolean;
    status: boolean;
  };

  onlineCategorySettings: {
    categoryId: [];
    showOnline: boolean;
    status: boolean;
  };
  restaurantId: number;
}

export interface GeneralSettingsListResponseType extends BaseResponseType {
  data: GeneralSettingsType | null;
}

export interface GeneralSettingsListRequestType {
  restaurantId: number;
}

export interface GeneralSettingsResponseType extends BaseResponseType {
  data: GeneralSettingsType | null;
}

export enum MenuModeType {
  HIERARCHICAL = 'hierarchical',
  ONE_VIEW = 'one-view',
}

export interface IMenuModeSettings {
  table_serve_menu_mode: MenuModeType;
  quick_serve_menu_mode: MenuModeType;
}

export enum BreaksSetting {
  NO_BREAKS = 'no_breaks',
  COMPLIANT = 'compliant',
  NON_COMPLIANT = 'non_compliant',
}

export interface StickyPrinterReceiptSettings {
  dineIn?: boolean;
  pickup?: boolean;
  delivery?: boolean;
}

export interface TipPayoutSettings {
  tipConfigurationPaymentType?: TipConfigurationPaymentType;
}
